import React, { useState } from 'react';
import { ApolloCache, useApolloClient, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { ListOption, TagOption } from '../contacts/filters/types';
import { TargetPicker } from '../tagging/TargetPicker';
import { describeChangeError, forgetContactData, MAX_TAGS_PER_CHANGE, Target } from '../tagging/tagging';
import { useDebounced } from '../tagging/useDebounced';
import {
  CRM_ADD_CONTACT_TAGS,
  CRM_ADD_TO_LIST,
  CRM_CREATE_LIST,
  CRM_CREATE_TAG,
  CRM_LIST_CHOICES,
  CRM_REMOVE_CONTACT_TAGS,
  CRM_REMOVE_FROM_LIST,
  CRM_TAG_CHOICES,
} from '../tagging/tagging.queries';
import { ContactDetail, ContactListMembership, ContactTag, displayName, formatDay, SOURCE_WORDS } from './contactDetail';
import { CRM_CONTACT_DETAIL } from './contactDetail.queries';

/* ------------------------------------------------------------------ *
 * The two dialogs both cards use
 * ------------------------------------------------------------------ */

interface AddDialogProps {
  title: string;
  noun: 'tag' | 'list';
  choices: Array<{ id: number; name: string; color?: string | null; count?: number }>;
  loading: boolean;
  max: number;
  /** Makes what is new, then adds; throws what the API refused. */
  onAdd: (targets: Array<Target>) => Promise<void>;
  onClose: () => void;
  onSearch?: (text: string) => void;
}

const AddDialog = ({ title, noun, choices, loading, max, onAdd, onClose, onSearch }: AddDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [targets, setTargets] = useState<Array<Target>>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>();

  const add = async () => {
    setRunning(true);
    setError(undefined);
    try {
      await onAdd(targets);
      onClose();
    } catch (err) {
      setError(err);
      setRunning(false);
    }
  };

  return (
    <Dialog
      open
      onClose={running ? undefined : onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="xs"
      aria-labelledby="crm-add-title"
    >
      <DialogTitle id="crm-add-title">{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
        <TargetPicker
          noun={noun}
          choices={choices}
          selected={targets}
          onChange={setTargets}
          max={max}
          allowCreate
          loading={loading}
          onQueryChange={onSearch}
          emptyText={loading ? 'Loading…' : undefined}
          footer={
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              The numbers are how many contacts {noun === 'tag' ? 'carry each tag' : 'are on each list'}.
            </Typography>
          }
        />
        {error !== undefined && <Alert severity="error">{describeChangeError(error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={running}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={add}
          disabled={running || targets.length === 0}
          startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
        >
          {noun === 'tag' ? (targets.length > 1 ? `Add ${targets.length} tags` : 'Add tag') : 'Add to list'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface RemoveDialogProps {
  title: string;
  text: string;
  confirm: string;
  onRemove: () => Promise<void>;
  onClose: () => void;
}

/** A removal is confirmed: it is logged, but a tag put back gets a new date, not its old one. */
const RemoveDialog = ({ title, text, confirm, onRemove, onClose }: RemoveDialogProps) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<unknown>();

  const remove = async () => {
    setRunning(true);
    setError(undefined);
    try {
      await onRemove();
      onClose();
    } catch (err) {
      setError(err);
      setRunning(false);
    }
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="xs" aria-labelledby="crm-remove-title">
      <DialogTitle id="crm-remove-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{text}</DialogContentText>
        {error !== undefined && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {describeChangeError(error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          Keep it
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={remove}
          disabled={running}
          startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
        >
          {confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const chipSx = (color: string | null) => ({
  height: 26,
  maxWidth: '100%',
  fontWeight: 500,
  color: color ?? 'text.primary',
  bgcolor: color ? alpha(color, 0.1) : '#f1f5f9',
  border: '1px solid',
  borderColor: color ? alpha(color, 0.35) : 'divider',
  '& .MuiChip-deleteIcon': { color: color ? alpha(color, 0.6) : undefined },
});

const added = (at: string, source: string) =>
  `Added ${formatDay(at)}${source && source !== 'MANUAL' ? ` from ${SOURCE_WORDS[source] ?? source.toLowerCase()}` : ''}`;

/* ------------------------------------------------------------------ *
 * Tags
 * ------------------------------------------------------------------ */

/**
 * Puts the tags a change answered with on the page's cached contact. Written
 * out rather than left to Apollo's normalising, which merges them only when
 * the answer and the page share a cache id.
 */
const redrawTags = (cache: ApolloCache<unknown>, id: number, tags: Array<ContactTag> | undefined) => {
  if (!tags) return;
  cache.updateQuery<{ crmContact: ContactDetail }>({ query: CRM_CONTACT_DETAIL, variables: { id } }, (cached) =>
    cached ? { crmContact: { ...cached.crmContact, tags } } : cached
  );
};

/**
 * The person's tags: added and removed one person at a time (card 15's
 * crmAddContactTags / crmRemoveContactTags, which return the contact, so the
 * chips redraw from the answer). System tags are shown and cannot be changed.
 * A merged-away record cannot be tagged; the contact it became can.
 */
export const ContactTags = ({ contact }: { contact: ContactDetail }) => {
  const client = useApolloClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ContactTag | null>(null);
  const choicesQuery = useQuery<{ crmTags: Array<TagOption> }>(CRM_TAG_CHOICES, { skip: !adding });

  const has = new Set(contact.tags.map((tag) => tag.tagId));
  const choices = (choicesQuery.data?.crmTags ?? [])
    .filter((tag) => !tag.isSystem && !has.has(tag.id))
    .map((tag) => ({ id: tag.id, name: tag.name, color: tag.color, count: tag.contactCount }));

  const add = async (targets: Array<Target>) => {
    const ids: Array<number> = [];
    for (const target of targets) {
      if (target.id !== undefined) ids.push(target.id);
      else {
        const { data } = await client.mutate<{ crmCreateTag: TagOption }>({
          mutation: CRM_CREATE_TAG,
          variables: { name: target.name },
        });
        ids.push(data!.crmCreateTag.id);
      }
    }
    await client.mutate<Record<string, { tags: Array<ContactTag> }>>({
      mutation: CRM_ADD_CONTACT_TAGS,
      variables: { contactId: contact.id, tagIds: ids },
      update: (cache, { data }) => redrawTags(cache, contact.id, data?.crmAddContactTags.tags),
    });
    forgetContactData(client.cache, 'onePerson');
  };

  const remove = async (tag: ContactTag) => {
    await client.mutate<Record<string, { tags: Array<ContactTag> }>>({
      mutation: CRM_REMOVE_CONTACT_TAGS,
      variables: { contactId: contact.id, tagIds: [tag.tagId] },
      update: (cache, { data }) => redrawTags(cache, contact.id, data?.crmRemoveContactTags.tags),
    });
    forgetContactData(client.cache, 'onePerson');
  };

  const merged = contact.mergedIntoId != null;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        {contact.tags.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tags.
          </Typography>
        )}
        {contact.tags.map((tag) => (
          <Tooltip key={tag.tagId} title={`${added(tag.addedAt, tag.source)}${tag.isSystem ? '. Set by automation.' : ''}`}>
            <Chip
              size="small"
              label={tag.name}
              onDelete={tag.isSystem ? undefined : () => setRemoving(tag)}
              deleteIcon={
                tag.isSystem ? undefined : (
                  <span role="button" aria-label={`Remove ${tag.name}`}>
                    ×
                  </span>
                )
              }
              sx={{
                ...chipSx(tag.color),
                '& .MuiChip-deleteIcon': { fontSize: 16, lineHeight: 1, px: 0.5, color: 'inherit', opacity: 0.6 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={merged} sx={{ mt: 1, ml: -0.5 }}>
        Add tag
      </Button>
      {merged && (
        <Typography variant="caption" color="text.secondary" component="p">
          This record was merged into another contact. Tag that contact instead.
        </Typography>
      )}

      {adding && (
        <AddDialog
          title={`Tag ${displayName(contact)}`}
          noun="tag"
          choices={choices}
          loading={choicesQuery.loading}
          max={MAX_TAGS_PER_CHANGE}
          onAdd={add}
          onClose={() => setAdding(false)}
        />
      )}
      {removing && (
        <RemoveDialog
          title={`Remove ${removing.name}?`}
          text={`${added(removing.addedAt, removing.source)}. The removal is logged on the timeline with your name. Adding the tag again gives it today's date.`}
          confirm="Remove tag"
          onRemove={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ *
 * Lists
 * ------------------------------------------------------------------ */

interface ContactListsProps {
  contact: ContactDetail;
  /** Asks the contact again: list changes answer with the list, not the contact. */
  onChanged: () => void;
}

/** The lists the person is on (static lists; saved filters are not memberships). */
export const ContactLists = ({ contact, onChanged }: ContactListsProps) => {
  const client = useApolloClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ContactListMembership | null>(null);
  const [text, setText] = useState('');
  const search = useDebounced(text.trim());
  const choicesQuery = useQuery<{ crmSegments: { total: number; items: Array<ListOption> } }>(CRM_LIST_CHOICES, {
    variables: search ? { search } : {},
    skip: !adding,
  });

  const on = new Set(contact.lists.map((list) => list.segmentId));
  const choices = (choicesQuery.data?.crmSegments.items ?? [])
    .filter((list) => !on.has(list.id))
    .map((list) => ({ id: list.id, name: list.name, count: list.contactCount }));
  const searchApi = (choicesQuery.data?.crmSegments.total ?? 0) > 200 || Boolean(search);

  const add = async ([target]: Array<Target>) => {
    let listId = target.id;
    if (listId === undefined) {
      const { data } = await client.mutate<{ crmCreateList: ListOption }>({
        mutation: CRM_CREATE_LIST,
        variables: { name: target.name },
      });
      listId = data!.crmCreateList.id;
    }
    await client.mutate({ mutation: CRM_ADD_TO_LIST, variables: { listId, contactIds: [contact.id] } });
    forgetContactData(client.cache, 'onePerson');
    onChanged();
  };

  const remove = async (list: ContactListMembership) => {
    await client.mutate({ mutation: CRM_REMOVE_FROM_LIST, variables: { listId: list.segmentId, contactIds: [contact.id] } });
    forgetContactData(client.cache, 'onePerson');
    onChanged();
  };

  const merged = contact.mergedIntoId != null;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        {contact.lists.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Not on any list.
          </Typography>
        )}
        {contact.lists.map((list) => (
          <Tooltip key={list.segmentId} title={added(list.addedAt, list.source)}>
            <Chip
              size="small"
              label={list.name}
              onDelete={() => setRemoving(list)}
              deleteIcon={
                <span role="button" aria-label={`Take off ${list.name}`}>
                  ×
                </span>
              }
              sx={{
                ...chipSx(null),
                '& .MuiChip-deleteIcon': { fontSize: 16, lineHeight: 1, px: 0.5, color: 'inherit', opacity: 0.6 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={merged} sx={{ mt: 1, ml: -0.5 }}>
        Add to list
      </Button>

      {adding && (
        <AddDialog
          title={`Add ${displayName(contact)} to a list`}
          noun="list"
          choices={choices}
          loading={choicesQuery.loading}
          max={1}
          onAdd={add}
          onClose={() => {
            setAdding(false);
            setText('');
          }}
          onSearch={searchApi ? setText : undefined}
        />
      )}
      {removing && (
        <RemoveDialog
          title={`Take off ${removing.name}?`}
          text={`${added(removing.addedAt, removing.source)}. Adding them again gives today's date.`}
          confirm="Take off list"
          onRemove={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};
